import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Navigate, useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Box, CircularProgress, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { ExternalLink } from "lucide-react";
import { toast } from "react-toastify";

import { AlertNote } from "../../components/AlertNote";
import { Badge } from "../../components/Badge";
import { Button } from "../../components/Button";
import { Card } from "../../components/Card";
import { Progress } from "../../components/Progress";
import { useAuth } from "../../components/contexts/AuthContext";
import { hasCommercialPlan } from "../../utils/agencyFeatures";
import {
  BILLING_QUERY_KEY,
  changePlan,
  createCheckoutSession,
  createPortalSession,
  getBilling,
  keepPlan,
  purchaseHours,
  setExtraUsers,
} from "../../services/billingService";
import { useHoursCheckoutReturn } from "../../hooks/useHoursCheckoutReturn";
import { useStripeRedirect } from "../../hooks/useStripeRedirect";
import { useStripeReturn } from "../../hooks/useStripeReturn";
import { hoursExpectation } from "../../hooks/stripeReturn";
import { BILLING_DETAILS_PATH } from "../Settings/settingsPaths";
import { ChangePlanDialog } from "./ChangePlanDialog";
import { ExtraUsersDialog } from "./ExtraUsersDialog";
import { BuyHoursDialog } from "./BuyHoursDialog";
import { PlanPicker } from "./PlanPicker";
import { formatMinutes } from "../../utils/numberFormat";
import {
  billingStatus,
  formatDate,
  formatPrice,
  formatShortDate,
  formatShortDateWithYear,
  lowestHourPrice,
  pluralize,
} from "./billingFormat";

// Barra de uso; `extraFrom` (%) marca con un separador y rayas la parte que viene de plazas extra pagadas.
const UsageBar = ({ percent, extraFrom }) => {
  const theme = useTheme();
  const primary = theme.palette.primary.main;
  return (
    <Box sx={{ position: "relative", flex: 1, minWidth: 0 }}>
      <Progress value={percent} />
      {extraFrom != null && (
        <>
          <Box
            sx={{
              position: "absolute",
              left: `${extraFrom}%`,
              right: 0,
              top: 0,
              height: 8,
              borderRadius: "0 9999px 9999px 0",
              pointerEvents: "none",
              background: `repeating-linear-gradient(135deg, ${alpha(primary, 0.4)} 0 3px, transparent 3px 7px)`,
            }}
          />
          <Box
            sx={{
              position: "absolute",
              left: `${extraFrom}%`,
              top: -3,
              height: 14,
              width: 2,
              ml: "-1px",
              borderRadius: 1,
              backgroundColor: theme.palette.text.primary,
            }}
          />
        </>
      )}
    </Box>
  );
};

UsageBar.propTypes = { percent: PropTypes.number.isRequired, extraFrom: PropTypes.number };

const UsageColumn = ({ label, value, limit, tag, percent, extraFrom, caption, control }) => {
  const theme = useTheme();
  const hasBar = percent != null;
  const tagNode = tag && (
    <Typography sx={{ fontSize: "12px", fontWeight: 600, color: tag.color || theme.palette.text.secondary }}>
      {tag.label}
    </Typography>
  );
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25, p: 3, minWidth: 0 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 1 }}>
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontSize: "13px" }}>{label}</Typography>
        {hasBar && tagNode}
      </Box>
      {/* Misma altura en las tres columnas: la barra y el valor de horas quedan alineados */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minHeight: 24 }}>
        {hasBar ? (
          <>
            <UsageBar percent={percent} extraFrom={extraFrom} />
            <Typography sx={{ fontSize: "15px", whiteSpace: "nowrap" }}>
              <strong>{value}</strong>/{limit}
            </Typography>
          </>
        ) : (
          <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75, flexWrap: "wrap" }}>
            <Typography sx={{ fontWeight: 700, fontSize: "15px" }}>{value}</Typography>
            {tagNode}
          </Box>
        )}
      </Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 1, minHeight: 36, flexWrap: "wrap", mt: "auto" }}>
        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: "12px" }}>{caption}</Typography>
        {control && <Box sx={{ ml: "auto" }}>{control}</Box>}
      </Box>
    </Box>
  );
};

UsageColumn.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node,
  limit: PropTypes.number,
  tag: PropTypes.shape({ label: PropTypes.string, color: PropTypes.string }),
  percent: PropTypes.number,
  extraFrom: PropTypes.number,
  caption: PropTypes.node,
  control: PropTypes.node,
};

const usagePercent = (used, limit) => (limit ? Math.min(100, (used / limit) * 100) : null);

const PlanOverviewCard = ({
  billing,
  extraUserPrice,
  hours,
  busy,
  disabled,
  onPortal,
  onKeep,
  onManageUsers,
  onBuyHours,
  onCancel,
}) => {
  const theme = useTheme();
  const danger = theme.palette.error.main;
  const primary = theme.palette.primary.main;
  const {
    plan_name: planName,
    is_trial: isTrial,
    has_subscription: hasSubscription,
    days_remaining: days,
    scheduled_plan_name: scheduledName,
    scheduled_extra_users: scheduledExtraUsers,
    seats,
  } = billing;
  const status = billingStatus(billing);
  const pastDue = status === "past_due";
  const cancelling = status === "cancelling";
  // Bajada programada al final del periodo (schedule en Stripe) a un plan de pago inferior
  const downgrading = status === "downgrading";
  const portalBusy = busy?.kind === "portal";
  // Los usuarios adicionales van en una suscripción de Stripe aparte, con su propio estado y cobro
  const seatsStatus = billingStatus(seats);
  const seatsLive = seats.has_subscription;
  const seatsPastDue = seatsStatus === "past_due";
  const seatsCancelling = seatsStatus === "cancelling";
  const seatsDowngrading = scheduledExtraUsers != null;
  const actionsDisabled = Boolean(busy) || disabled;

  const badge = (() => {
    if (pastDue) return { color: "blocked", label: "Pago pendiente" };
    if (cancelling) return { color: "pending", label: `Se cancela el ${formatShortDate(billing.current_period_end)}` };
    if (downgrading) return { color: "pending", label: `Pasa a ${scheduledName} el ${formatShortDate(billing.current_period_end)}` };
    if (isTrial) return { color: days <= 3 ? "pending" : "active", label: pluralize(days, "día restante", "días restantes") };
    return null;
  })();

  const nextInvoice = billing.next_invoice;
  const chargeLines = [
    (() => {
      if (isTrial || pastDue || cancelling || downgrading || !hasSubscription) return null;
      if (nextInvoice) return `Próximo cobro del plan: ${formatPrice(nextInvoice)} el ${formatDate(nextInvoice.date)}`;
      if (billing.current_period_end) return `Próxima renovación: ${formatDate(billing.current_period_end)}`;
      return null;
    })(),
    (() => {
      if (!seatsLive || seatsPastDue || seatsCancelling) return null;
      if (seats.next_invoice) return `Usuarios adicionales: ${formatPrice(seats.next_invoice)} el ${formatDate(seats.next_invoice.date)}`;
      if (seats.current_period_end) return `Usuarios adicionales: renovación el ${formatDate(seats.current_period_end)}`;
      return null;
    })(),
  ].filter(Boolean);

  const portalButton = (label, variant = "outline", withIcon = true) => (
    <Button variant={variant} size="sm" disabled={actionsDisabled} onClick={onPortal} sx={{ gap: 1 }}>
      {portalBusy ? (
        <>
          <CircularProgress size={14} color="inherit" /> Redirigiendo…
        </>
      ) : (
        <>
          {label} {withIcon && <ExternalLink size={14} />}
        </>
      )}
    </Button>
  );

  const keepButton = (cancelling || downgrading) && (
    <Button size="sm" disabled={actionsDisabled} onClick={() => onKeep("plan")} sx={{ gap: 1 }}>
      {busy?.kind === "keep_plan" ? (
        <>
          <CircularProgress size={14} color="inherit" /> Guardando…
        </>
      ) : (
        `Mantener ${planName}`
      )}
    </Button>
  );

  const keepSeatsButton = (
    <Button variant="outline" size="sm" disabled={actionsDisabled} onClick={() => onKeep("seats")}>
      {busy?.kind === "keep_seats" ? "Guardando…" : "Mantener plazas"}
    </Button>
  );

  const billingButton = (() => {
    if (pastDue) return portalButton("Actualizar método de pago", "default", false);
    if (hasSubscription && !cancelling) {
      return (
        <Button variant="outline" size="sm" disabled={actionsDisabled} onClick={onCancel}>
          Cancelar suscripción
        </Button>
      );
    }
    return null;
  })();

  // Clientes: en trial no hay límite; en Free con selección fijada se muestra el acceso completo.
  const { active_clients: activeClients, max_clients: maxClients } = billing;
  const clientsCaption = (() => {
    if (!maxClients) return "Sin límite durante la prueba";
    if (billing.plan === "free" && activeClients > maxClients) {
      return `${billing.editable_clients} con acceso completo · ${activeClients} visibles`;
    }
    return "Para más clientes, cambia de plan";
  })();

  const {
    included_users: includedUsers,
    extra_users: extraUsers,
    users_count: usersCount,
    users_limit: usersLimit,
  } = billing;
  const usersTag = (() => {
    if (seatsPastDue) return { label: "Pago pendiente", color: danger };
    if (seatsCancelling) return { label: `Plazas adicionales hasta el ${formatShortDate(seats.current_period_end)}` };
    if (seatsDowngrading) {
      return { label: `${pluralize(scheduledExtraUsers, "plaza adicional", "plazas adicionales")} desde el ${formatShortDate(seats.current_period_end)}` };
    }
    if (!billing.can_add_users) return { label: "Límite alcanzado", color: primary };
    return null;
  })();
  const usersCaption = (() => {
    if (usersLimit == null) return "Sin límite durante la prueba";
    const price = extraUserPrice ? ` (${formatPrice(extraUserPrice)} /mes cada una)` : "";
    if (extraUsers > 0) {
      return `${includedUsers} incluidos en tu plan + ${pluralize(extraUsers, "plaza adicional pagada", "plazas adicionales pagadas")}${price}`;
    }
    return extraUserPrice ? `${includedUsers} incluidos en tu plan · Plaza adicional: ${formatPrice(extraUserPrice)} /mes` : "";
  })();
  const columnButton = (label, onClick) => (
    <Button variant="outline" size="sm" disabled={actionsDisabled} onClick={onClick}>
      {label}
    </Button>
  );

  const columns = hours ? 3 : 2;

  return (
    <Card sx={{ gap: 0, overflow: "hidden" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          flexDirection: { xs: "column", sm: "row" },
          gap: 1.5,
          px: 3,
          py: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Typography sx={{ fontWeight: 700, fontSize: "15px" }}>Tu plan: {planName}</Typography>
            {badge && <Badge color={badge.color}>{badge.label}</Badge>}
          </Box>
          {chargeLines.map((line) => (
            <Typography key={line} variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: "12px" }}>
              {line}
            </Typography>
          ))}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
          {keepButton}
          {billingButton}
        </Box>
      </Box>

      {(pastDue || cancelling || downgrading || seatsPastDue || seatsCancelling || seatsDowngrading || isTrial) && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, px: 3, pt: 2 }}>
          {pastDue && (
            <AlertNote variant="danger">
              <strong>No hemos podido cobrar tu última factura.</strong> Actualiza tu método de pago para mantener el plan{" "}
              {planName}. Volveremos a intentarlo en los próximos días; si no se resuelve, tu agencia pasará al plan Free.
            </AlertNote>
          )}
          {cancelling && (
            <AlertNote variant="warning">
              Tu plan {planName} sigue activo hasta el {formatDate(billing.current_period_end)}. Después pasarás a Free:
              podrás elegir 3 clientes con acceso completo, dejarán de generarse informes mensuales, tus clientes dejarán de ver sus métricas, y tu logo y colores dejarán de verse (se guardan por si vuelves a un plan con marca blanca).
              {seatsLive && !seatsCancelling &&
                ` Mantienes tus ${pluralize(extraUsers, "usuario adicional", "usuarios adicionales")}, que se seguirán cobrando cada mes.`}
            </AlertNote>
          )}
          {downgrading && (
            <AlertNote variant="info">
              Tu plan {planName} sigue activo hasta el {formatDate(billing.current_period_end)}. Después pasarás al plan{" "}
              {scheduledName}. Por el cambio no se cobra ni se devuelve nada.
            </AlertNote>
          )}
          {seatsPastDue && (
            <AlertNote
              variant="danger"
              action={
                <Button variant="outline" size="sm" disabled={actionsDisabled} onClick={onPortal}>
                  Actualizar método de pago
                </Button>
              }
            >
              <strong>No hemos podido cobrar tus usuarios adicionales.</strong> Volveremos a intentarlo en los próximos
              días; si no se resuelve, perderás las {pluralize(extraUsers, "plaza adicional", "plazas adicionales")}, pero
              mantendrás tu plan.
            </AlertNote>
          )}
          {seatsCancelling && (
            <AlertNote variant="warning" action={keepSeatsButton}>
              Tus {pluralize(extraUsers, "usuario adicional", "usuarios adicionales")} se mantienen hasta el{" "}
              {formatDate(seats.current_period_end)}; después dejarán de cobrarse y solo tendrás las plazas incluidas en tu plan.
            </AlertNote>
          )}
          {seatsDowngrading && (
            <AlertNote variant="info" action={keepSeatsButton}>
              Mantienes tus {pluralize(extraUsers, "usuario adicional", "usuarios adicionales")} hasta el{" "}
              {formatDate(seats.current_period_end)}. Después pasarás a{" "}
              {pluralize(scheduledExtraUsers, "plaza adicional", "plazas adicionales")}. Por el cambio no se cobra ni se devuelve nada.
            </AlertNote>
          )}
          {isTrial && !pastDue && (
            <AlertNote variant="info">
              Tu prueba termina el {formatDate(billing.trial_ends_at)}. Si no contratas un plan, pasarás a Free: acceso
              completo a 3 clientes y el resto en solo lectura. Si contratas ahora, la prueba termina y se cobra el primer
              mes en ese momento.
            </AlertNote>
          )}
        </Box>
      )}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: `repeat(${columns}, minmax(0, 1fr))` },
          "& > * + *": {
            borderTop: { xs: `1px solid ${theme.palette.divider}`, md: "none" },
            borderLeft: { md: `1px solid ${theme.palette.divider}` },
          },
        }}
      >
        <UsageColumn
          label="Clientes activos"
          value={activeClients}
          limit={maxClients}
          tag={maxClients && activeClients >= maxClients ? { label: "Límite alcanzado", color: primary } : null}
          percent={usagePercent(activeClients, maxClients)}
          caption={clientsCaption}
        />
        <UsageColumn
          label="Usuarios del equipo"
          value={usersCount}
          limit={usersLimit}
          tag={usersTag}
          percent={usagePercent(usersCount, usersLimit)}
          extraFrom={extraUsers > 0 ? usagePercent(includedUsers, usersLimit) : null}
          caption={usersCaption}
          control={onManageUsers ? columnButton("Gestionar plazas", onManageUsers) : null}
        />
        {hours && (
          <UsageColumn
            label="Horas de incidencias"
            value={`${formatMinutes(hours.remaining_minutes)} disponibles`}
            tag={
              hours.remaining_minutes > 0
                ? { label: `hasta el ${formatShortDateWithYear(hours.expires_on)}` }
                : { label: "Sin horas para abrir incidencias", color: danger }
            }
            caption={hours.price ? `Desde ${formatPrice(lowestHourPrice(hours.price))} por hora` : null}
            control={hours.price ? columnButton("Comprar horas", onBuyHours) : null}
          />
        )}
      </Box>
    </Card>
  );
};

PlanOverviewCard.propTypes = {
  billing: PropTypes.object.isRequired,
  extraUserPrice: PropTypes.object,
  hours: PropTypes.object,
  busy: PropTypes.shape({ kind: PropTypes.string, plan: PropTypes.string }),
  disabled: PropTypes.bool,
  onPortal: PropTypes.func.isRequired,
  // Deshace la bajada o cancelación programada: "plan" | "seats"
  onKeep: PropTypes.func.isRequired,
  onManageUsers: PropTypes.func,
  onCancel: PropTypes.func.isRequired,
  onBuyHours: PropTypes.func.isRequired,
};

const StripeReturnNotice = ({ phase, subject, onRetry, onDismiss }) => {
  const theme = useTheme();
  const closeButton = (
    <Button variant="ghost" size="sm" onClick={onDismiss}>
      Cerrar
    </Button>
  );
  const retryButton = (label) => (
    <Button variant="outline" size="sm" onClick={onRetry}>
      {label}
    </Button>
  );
  if (phase === "cancel") {
    return (
      <AlertNote variant="info" action={closeButton}>
        Has cancelado el pago. No se ha realizado ningún cargo.
      </AlertNote>
    );
  }
  if (phase === "back") {
    return (
      <AlertNote variant="info" action={closeButton}>
        Has vuelto sin confirmar. No se ha hecho ningún cambio ni cargo.
      </AlertNote>
    );
  }
  if (phase === "timeout") {
    return (
      <AlertNote variant="info" action={retryButton("Volver a comprobar")}>
        Está tardando más de lo normal en confirmarse el cambio. Si lo has completado, tu plan se actualizará en
        unos minutos y te llegará un correo.
      </AlertNote>
    );
  }
  if (phase === "error") {
    return (
      <AlertNote variant="danger" action={retryButton("Reintentar")}>
        No hemos podido comprobar el cambio. Si lo has completado, se reflejará en unos minutos.
      </AlertNote>
    );
  }
  if (phase !== "confirming") return null;
  return (
    <Card sx={{ p: 3, flexDirection: "row", alignItems: "center", gap: 2 }}>
      <CircularProgress size={24} />
      <Box>
        <Typography sx={{ fontWeight: 600 }}>Confirmando el cambio…</Typography>
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
          Estamos actualizando {subject}. Tarda unos segundos; no cierres esta página.
        </Typography>
      </Box>
    </Card>
  );
};

StripeReturnNotice.propTypes = {
  phase: PropTypes.string,
  subject: PropTypes.string.isRequired,
  onRetry: PropTypes.func.isRequired,
  onDismiss: PropTypes.func.isRequired,
};

export function Plan() {
  const { user, refreshUser } = useAuth();
  const { setPageContext } = useOutletContext();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const agencyPlan = user?.agencyPlan;
  const allowed = hasCommercialPlan(user);

  const stripeReturn = useStripeReturn();
  const confirming = stripeReturn.phase === "confirming";
  const { go: goToStripe, detailsDialog } = useStripeRedirect();
  const [busy, setBusy] = useState(null);
  const [changeTarget, setChangeTarget] = useState(null);
  // Cancelar el plan es pasar a Free; las plazas sueltas se gestionan desde «Gestionar plazas»
  const handleCancel = () => setChangeTarget({ ...plans.find((plan) => plan.id === "free"), cancel: true });
  // `?add_users=1` (desde el aviso de límite) abre directamente la compra de plazas
  const [usersDialogOpen, setUsersDialogOpen] = useState(searchParams.get("add_users") === "1");
  const [hoursDialogOpen, setHoursDialogOpen] = useState(searchParams.get("buy_hours") === "1");

  useEffect(() => {
    setPageContext({
      title: "Plan y facturación",
      subtitle: "Consulta tu plan, compara opciones y gestiona tus pagos.",
      showTitle: true,
    });
  }, [setPageContext]);

  const { data, isError, refetch } = useQuery({
    queryKey: BILLING_QUERY_KEY,
    queryFn: getBilling,
    enabled: allowed,
  });

  useHoursCheckoutReturn();

  const plans = data?.plans;
  // `GET billing/` no trae los datos de prueba (días, fecha): salen de la sesión
  const billing = useMemo(() => {
    if (!agencyPlan) return null;
    const fromSession = { ...agencyPlan, next_invoice: null, seats: { ...agencyPlan.seats, next_invoice: null } };
    return data ? { ...fromSession, ...data, is_trial: data.plan === "trial" } : fromSession;
  }, [data, agencyPlan]);
  const canManageUsers = Boolean(data?.extra_user_price) && billing?.included_users != null;

  const returnSubject = (() => {
    const expectation = stripeReturn.expectation;
    if (!expectation) return "tu facturación";
    if (expectation.kind === "seats") return "tus usuarios adicionales";
    const name = plans?.find((plan) => plan.id === expectation.plan)?.name;
    return name ? `tu plan a ${name}` : "tu plan";
  })();

  // Resuelve false si la agencia se queda (cerró el modal de datos fiscales); el error se muestra en un toast
  const goWithBusy = async (nextBusy, request, expectation, errorMessage) => {
    setBusy(nextBusy);
    try {
      if (!(await goToStripe(request, expectation))) setBusy(null);
    } catch (requestError) {
      toast.error(requestError?.response?.data?.detail || errorMessage);
      setBusy(null);
    }
  };

  const handleCheckout = (plan) =>
    goWithBusy(
      { kind: "checkout", plan: plan.id },
      () => createCheckoutSession(plan.id),
      { kind: "plan", plan: plan.id },
      "No se ha podido iniciar el pago. Inténtalo de nuevo en unos minutos."
    );

  const handlePortal = () =>
    goWithBusy({ kind: "portal" }, createPortalSession, null, "No se ha podido abrir la facturación. Inténtalo de nuevo.");

  const clearParam = (name) => {
    if (!searchParams.get(name)) return;
    const next = new URLSearchParams(searchParams);
    next.delete(name);
    setSearchParams(next, { replace: true });
  };

  const closeUsersDialog = () => {
    setUsersDialogOpen(false);
    clearParam("add_users");
  };

  const closeHoursDialog = () => {
    setHoursDialogOpen(false);
    clearParam("buy_hours");
  };

  const handleKeep = async (kind) => {
    setBusy({ kind: `keep_${kind}` });
    try {
      queryClient.setQueryData(BILLING_QUERY_KEY, await keepPlan(kind));
      await refreshUser();
      toast.success(kind === "seats" ? "Mantienes tus usuarios adicionales." : `Mantienes el plan ${billing.plan_name}.`);
    } catch (requestError) {
      const { code, detail } = requestError?.response?.data || {};
      // Ya se deshizo (otra pestaña o el portal): el resumen estaba desfasado
      if (code === "nothing_scheduled") refetch();
      toast.error(detail || "No se ha podido mantener el cambio. Inténtalo de nuevo.");
    } finally {
      setBusy(null);
    }
  };

  if (!allowed) return <Navigate to="/dashboard" replace />;

  const currentCatalogPlan = plans?.find((plan) => plan.id === billing.plan);
  const actionsDisabled = isError || confirming;

  return (
    <Box sx={{ display: "grid", gap: 3, maxWidth: 1120 }}>
      <StripeReturnNotice
        phase={stripeReturn.phase}
        subject={returnSubject}
        onRetry={stripeReturn.retry}
        onDismiss={stripeReturn.dismiss}
      />

      {isError && (
        <AlertNote
          variant="danger"
          action={
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              Reintentar
            </Button>
          }
        >
          No se ha podido cargar la información de facturación.
        </AlertNote>
      )}

      {data && !data.billing_details_complete && (
        <AlertNote
          variant="warning"
          action={
            <Button variant="outline" size="sm" onClick={() => navigate(BILLING_DETAILS_PATH)}>
              Completar datos
            </Button>
          }
        >
          Antes de pagar necesitamos tus datos fiscales (nombre o razón social, NIF y dirección).
        </AlertNote>
      )}

      {!confirming && (
        <PlanOverviewCard
          billing={billing}
          extraUserPrice={data?.extra_user_price}
          hours={data?.hours}
          busy={busy}
          disabled={actionsDisabled}
          onPortal={handlePortal}
          onKeep={handleKeep}
          onManageUsers={canManageUsers ? () => setUsersDialogOpen(true) : null}
          onBuyHours={() => setHoursDialogOpen(true)}
          onCancel={handleCancel}
        />
      )}

      <PlanPicker
        plans={plans}
        billing={billing}
        busy={busy}
        disabled={actionsDisabled}
        onCheckout={handleCheckout}
        onChangePlan={setChangeTarget}
      />

      <ChangePlanDialog
        open={Boolean(changeTarget)}
        onClose={() => setChangeTarget(null)}
        billing={billing}
        currentPlan={currentCatalogPlan}
        targetPlan={changeTarget}
        onConfirm={(target) =>
          // Con plazas contratadas, el portal permite cancelar plan y plazas en una sola visita
          target.cancel && billing.seats.has_subscription
            ? goToStripe(createPortalSession, null)
            : goToStripe(() => changePlan(target.id), { kind: "plan", plan: target.id })
        }
      />

      <ExtraUsersDialog
        open={usersDialogOpen && canManageUsers}
        onClose={closeUsersDialog}
        billing={billing}
        price={data?.extra_user_price}
        onConfirm={(quantity) => goToStripe(() => setExtraUsers(quantity), { kind: "seats", quantity })}
      />

      {hoursDialogOpen && data && (
        <BuyHoursDialog
          hours={data.hours}
          onClose={closeHoursDialog}
          onCheckout={(quantity) =>
            goToStripe(() => purchaseHours(quantity), hoursExpectation(quantity, data.hours.remaining_minutes))
          }
        />
      )}

      {detailsDialog}
    </Box>
  );
}
